export type ImageLoader = 's3' | 'xhr' | 'none';

export interface ImageLoaderOptions {
  fromRelace?: boolean | undefined;
  fromPOTemplate?: boolean | undefined;
  noImageAsset?: string | undefined;
}

const S3_HOST_SUFFIX = 'amazonaws.com';
const S3_REGION_HOST = 's3.eu-west-2.amazonaws.com';
const S3_HOST_PREFIX = 's3.';

/**
 * Decides which of ImgDropzone's two image loaders should hydrate the crop surface.
 *
 * The two loaders are mutually exclusive: the S3 loader fetches a presigned object with CORS
 * and converts it to a File, while the XHR loader pulls anything else off the static path.
 * Running both against the same value races them onto the same `files` state and leaves the
 * winner undefined, so exactly one is chosen here.
 * @param image - Image value handed to ImgDropzone: an S3 URL, a stored key, or a placeholder.
 * @param options - Flow flags and the placeholder asset.
 * @returns the loader to run for this value.
 */
export const resolveImageLoader = (image: unknown, options: ImageLoaderOptions = {}): ImageLoader => {
  const { fromRelace = false, fromPOTemplate = false, noImageAsset = '' } = options;

  if (typeof image !== 'string' || image === '') {
    return 'none';
  }

  if (noImageAsset && image === noImageAsset) {
    return 'none';
  }

  if (image.includes(S3_REGION_HOST) || (image.includes(S3_HOST_PREFIX) && image.includes(S3_HOST_SUFFIX))) {
    return 's3';
  }

  if (fromRelace || fromPOTemplate) {
    return 'none';
  }

  return 'xhr';
};

export default resolveImageLoader;
